"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter, usePathname } from "@rangojs/router/client";
import type { ShopperProductsTypes } from "commerce-sdk-isomorphic";

type Variant = ShopperProductsTypes.Variant;
type VariationAttribute = ShopperProductsTypes.VariationAttribute;

export interface VariantProduct {
  id: string;
  name?: string;
  price?: number;
  currency?: string;
  type?: ShopperProductsTypes.ProductType;
  variationAttributes?: VariationAttribute[];
  variants?: Variant[];
  variationGroups?: { productId: string; variationValues?: Record<string, string> }[];
  c_imageGroups?: any[];
  imageGroups?: any[];
  longDescription?: string;
  pageDescription?: string;
  primaryCategoryId?: string;
}

export interface VariantContextValue {
  product: VariantProduct;
  selected: Record<string, string>;
  setSelected: (type: string, value: string) => void;
  variant: Variant | undefined;
  allSelected: boolean;
  /** Price for the selected variant, or master product price */
  price: number | undefined;
  /** Currency code */
  currency: string;
  /** Images for the current selection (filtered by variant attrs) */
  images: { link: string; alt?: string }[];
  /** Whether the selected variant is orderable */
  orderable: boolean;
  /** Product ID to use for add-to-cart */
  productId: string | undefined;
}

const VariantCtx = createContext<VariantContextValue | null>(null);

export function useVariant() {
  const ctx = useContext(VariantCtx);
  if (!ctx) throw new Error("useVariant must be inside VariantProvider");
  return ctx;
}

function getSelectedVariant(
  product: VariantProduct,
  selected: Record<string, string>,
): Variant | undefined {
  const attrCount = product.variationAttributes?.length ?? 0;
  if (Object.keys(selected).length !== attrCount) return undefined;
  return product.variants?.find((v) =>
    Object.keys(selected).every((key) => v.variationValues?.[key] === selected[key]),
  );
}

function getVariantImages(
  product: VariantProduct,
  selected: Record<string, string>,
): { link: string; alt?: string }[] {
  const imageGroups = product.c_imageGroups ?? product.imageGroups ?? [];

  // Try to find an image group matching the selected variant attributes
  if (Object.keys(selected).length > 0) {
    const matched = imageGroups.find(
      (g: any) =>
        g.viewType === "large" &&
        g.variationAttributes?.some((va: any) =>
          va.values?.some((v: any) => selected[va.id] && v.value === selected[va.id]),
        ),
    );
    if (matched?.images?.length) return matched.images;
  }

  // Fallback to first large group or first group
  const fallback = imageGroups.find((g: any) => g.viewType === "large") ?? imageGroups[0];
  return fallback?.images ?? [];
}

export function VariantProvider({
  product,
  initialSelected,
  children,
}: {
  product: VariantProduct;
  initialSelected?: Record<string, string>;
  children: ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const userChanged = useRef(false);

  const attrIds = useMemo(() => product.variationAttributes?.map((a) => a.id) ?? [], [product]);

  // Initialize from server-provided selection
  const [selected, setSelectedState] = useState(() => initialSelected ?? {});

  // Re-initialize when initialSelected changes (SPA navigation to new product)
  const [prevInitial, setPrevInitial] = useState(initialSelected);
  if (prevInitial !== initialSelected) {
    setPrevInitial(initialSelected);
    if (!userChanged.current) {
      setSelectedState(initialSelected ?? {});
    }
    userChanged.current = false;
  }

  // Sync to URL only when user manually changes a variant
  useEffect(() => {
    if (!userChanged.current) return;
    userChanged.current = false;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(selected)) {
      if (value) params.set(key, value);
    }
    const qs = params.toString();
    router.replace(pathname + (qs ? `?${qs}` : ""), { revalidate: false, scroll: false });
  }, [selected, router, pathname]);

  const setSelected = useCallback((type: string, value: string) => {
    userChanged.current = true;
    setSelectedState((prev) => ({ ...prev, [type]: value }));
  }, []);

  const variant = useMemo(() => getSelectedVariant(product, selected), [product, selected]);

  const allSelected = useMemo(
    () => attrIds.length === Object.keys(selected).length,
    [attrIds, selected],
  );

  const images = useMemo(() => getVariantImages(product, selected), [product, selected]);

  const price = variant?.price ?? product.price;
  const currency = product.currency ?? "GBP";
  const orderable = variant ? variant.orderable !== false : false;
  const productId = variant?.productId ?? (allSelected ? undefined : undefined);

  const value = useMemo<VariantContextValue>(
    () => ({
      product,
      selected,
      setSelected,
      variant,
      allSelected,
      price,
      currency,
      images,
      orderable,
      productId,
    }),
    [
      product,
      selected,
      setSelected,
      variant,
      allSelected,
      price,
      currency,
      images,
      orderable,
      productId,
    ],
  );

  return <VariantCtx.Provider value={value}>{children}</VariantCtx.Provider>;
}
